import type {
  CreateEvent,
  CreateEventSession,
  Event,
  EventHostStats,
  EventQuestionDto,
  EventSession,
  EventSessionQuery,
  QueryEventsReq,
  UpdateEvent,
  UpdateEventStatus,
  UpsertEventQuestions
} from "@garther/interfaces";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useApiClients } from "./context";

export const eventsKeys = {
  all: ["events"] as const,
  lists: () => [...eventsKeys.all, "list"] as const,
  list: (filters: QueryEventsReq) => [...eventsKeys.lists(), filters] as const,
  details: () => [...eventsKeys.all, "detail"] as const,
  detail: (id: string) => [...eventsKeys.details(), id] as const,
  sessions: (eventId: string, filter?: string) => [...eventsKeys.all, "sessions", eventId, filter] as const,
  questions: (eventId: string) => [...eventsKeys.all, "questions", eventId] as const,
  ticketTierTemplates: (eventId: string) => [...eventsKeys.all, "ticket-tier-templates", eventId] as const,
  hostStats: (hostId: string) => [...eventsKeys.all, "host-stats", hostId] as const
};

type UseEventsOptions = {
  enabled?: boolean;
} & Partial<QueryEventsReq>;

export function useEvents(options: UseEventsOptions = {}) {
  const { eventsApiClient } = useApiClients();
  const { limit = 6, page = 1, enabled = true } = options;

  return useQuery({
    queryKey: eventsKeys.list({ limit, page, ...options }),
    queryFn: async () => {
      const queryOptions: QueryEventsReq = { limit, page, ...options };
      return eventsApiClient.list(queryOptions);
    },
    enabled,
    staleTime: 5 * 60 * 1000
  });
}

export function useEvent(eventId: string, enabled = true) {
  const { eventsApiClient } = useApiClients();

  return useQuery({
    queryKey: eventsKeys.detail(eventId),
    queryFn: () => eventsApiClient.getOne(eventId),
    enabled: enabled && !!eventId,
    staleTime: 10 * 60 * 1000
  });
}

export function useEventsPagination(options: UseEventsOptions = {}) {
  const { enabled = true } = options;
  const [currentPage, setCurrentPage] = useState(1);

  const query = useEvents({ ...options, page: currentPage, enabled });
  const setPage = useCallback((page: number) => setCurrentPage(page), []);

  return {
    ...query,
    currentPage,
    setPage,
    pagination: query.data?.meta || null,
    events: query.data?.data || []
  };
}

export function useInfiniteEvents(options: UseEventsOptions = {}) {
  const { eventsApiClient } = useApiClients();
  const { limit = 9, enabled = true, ...filters } = options;

  return useInfiniteQuery({
    queryKey: [...eventsKeys.lists(), "infinite", { limit, ...filters }],
    queryFn: ({ pageParam }) => eventsApiClient.list({ ...filters, page: pageParam, limit }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.meta.hasNext ? lastPage.meta.page + 1 : undefined),
    enabled,
    staleTime: 5 * 60 * 1000
  });
}

export function useEventsInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: eventsKeys.all }),
    invalidateList: () => queryClient.invalidateQueries({ queryKey: eventsKeys.lists() }),
    invalidateEvent: (eventId: string) =>
      queryClient.invalidateQueries({ queryKey: eventsKeys.detail(eventId) }),
    invalidateEventSessions: (eventId: string) =>
      queryClient.invalidateQueries({ queryKey: eventsKeys.sessions(eventId) }),
    invalidateEventQuestions: (eventId: string) =>
      queryClient.invalidateQueries({ queryKey: eventsKeys.questions(eventId) })
  };
}

export function useCreateEventMutation(
  options: { onSuccess?: (result: Event) => void; onError?: (error: unknown) => void } = {}
) {
  const { eventsApiClient } = useApiClients();
  return useMutation({
    mutationFn: (data: CreateEvent) => eventsApiClient.createEvent(data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useUpdateEventMutation(
  options: { onSuccess?: (result: Event) => void; onError?: (error: unknown) => void } = {}
) {
  const { eventsApiClient } = useApiClients();
  const invalidate = useEventsInvalidation();
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: UpdateEvent }) =>
      eventsApiClient.updateEvent(eventId, data),
    onSuccess: (result, variables) => {
      invalidate.invalidateEvent(variables.eventId);
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useUpdateEventStatusMutation(
  options: { onSuccess?: (result: Event) => void; onError?: (error: unknown) => void } = {}
) {
  const { eventsApiClient } = useApiClients();
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: UpdateEventStatus }) =>
      eventsApiClient.updateEventStatus(eventId, data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useCreateSessionMutation(
  options: { onSuccess?: (result: EventSession) => void; onError?: (error: unknown) => void } = {}
) {
  const { eventsApiClient } = useApiClients();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: CreateEventSession }) =>
      eventsApiClient.createSession(eventId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.sessions(variables.eventId) });
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useUpsertEventQuestionsMutation(
  options: {
    onSuccess?: (
      result: EventQuestionDto[],
      variables: { eventId: string; data: UpsertEventQuestions }
    ) => void;
    onError?: (error: unknown) => void;
  } = {}
) {
  const { eventsApiClient } = useApiClients();
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: UpsertEventQuestions }) =>
      eventsApiClient.upsertQuestions(eventId, data),
    onSuccess: (result, variables) => {
      options.onSuccess?.(result, variables);
    },
    onError: (error) => options.onError?.(error)
  });
}

export type SessionFilter = "upcoming" | "previous" | "all";

export function useEventSessions(
  eventId: string,
  filter: SessionFilter,
  options: { enabled?: boolean } = {}
) {
  const { eventsApiClient } = useApiClients();
  const now = new Date();
  const query: EventSessionQuery =
    filter === "upcoming" ? { from: now } : filter === "previous" ? { to: now } : {};

  return useQuery({
    queryKey: eventsKeys.sessions(eventId, filter),
    queryFn: () => eventsApiClient.getSessions(eventId, query),
    enabled: (options.enabled ?? true) && !!eventId
  });
}

export function useTicketTierTemplates(eventId: string, options: { enabled?: boolean } = {}) {
  const { eventsApiClient } = useApiClients();

  return useQuery({
    queryKey: eventsKeys.ticketTierTemplates(eventId),
    queryFn: () => eventsApiClient.getTicketTierTemplates(eventId),
    enabled: (options.enabled ?? false) && !!eventId,
    staleTime: 10 * 60 * 1000
  });
}

export function useEventQuestions(eventId: string, options: { enabled?: boolean } = {}) {
  const { eventsApiClient } = useApiClients();

  return useQuery({
    queryKey: eventsKeys.questions(eventId),
    queryFn: () => eventsApiClient.getQuestions(eventId),
    enabled: (options.enabled ?? true) && !!eventId,
    staleTime: 5 * 60 * 1000
  });
}

export function useEventHostStats(hostId: string, options: { enabled?: boolean } = {}) {
  const { eventsApiClient } = useApiClients();

  return useQuery<EventHostStats.Type>({
    queryKey: eventsKeys.hostStats(hostId),
    queryFn: () => eventsApiClient.getHostStats(hostId),
    enabled: (options.enabled ?? true) && !!hostId,
    staleTime: 5 * 60 * 1000
  });
}
